import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7 } from "styled-components";
import React from "react";
import type { SanityPageBuilderMediaOnlySection, SanityPageBuilderSection } from "@ouster/shared";
import { type Ratio } from "~/features/ui/aspect-ratio";
import { Media } from "~/features/ui/media";
import { VideoControls } from "~/features/ui/video/video-controls";
import { Wrap } from "~/features/ui/wrap";
import { toCraftMedia } from "~/features/sanity/media";
import { hasRenderableSanityMedia, toSupportedRatio } from "./media-figure";
type Props = {
  section: SanityPageBuilderSection & {
    loaded?: boolean;
    sectionContent: SanityPageBuilderMediaOnlySection;
  };
};

// Width variants supported by the media renderer.
const assetLayoutVariants = {
  full: {
    "width": "100%"
  },
  large: {
    "@media (min-width: 1024px)": {
      "marginLeft": "auto",
      "marginRight": "auto",
      "width": "75%"
    }
  },
  medium: {
    "@media (min-width: 1024px)": {
      "marginLeft": "auto",
      "marginRight": "auto",
      "width": "50%"
    }
  }
} as const;
type AssetLayout = keyof typeof assetLayoutVariants;
function sectionAttrs(section: Props["section"]) {
  const {
    sectionSettings
  } = section;
  return {
    id: sectionSettings?.sectionId?.current || sectionSettings?.anchorName || section._key,
    "data-sanity-section-key": section._key,
    "data-sanity-section-type": section._type,
    "data-anchor-name": sectionSettings?.anchorName
  };
}
function BoxedMedia({
  section
}: Props) {
  const {
    sectionContent,
    sectionSettings
  } = section;
  const mode = sectionSettings?.mode || "light";
  const theme = sectionSettings?.theme || "white";
  const assetLayout = (sectionContent.assetLayout || "full") as AssetLayout;
  const craftMedia = toCraftMedia(sectionContent.media);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [video, setVideo] = React.useState<HTMLVideoElement | null>(null);
  const [videoRatio, setVideoRatio] = React.useState("2/1");
  const setVideoRef = React.useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
    setVideo(el);
  }, []);

  // Inline videos keep their intrinsic ratio (measured once metadata is available).
  React.useEffect(() => {
    if (!video || typeof video.addEventListener !== "function") {
      return;
    }
    function getRatio() {
      if (!video) {
        return;
      }
      const {
        videoWidth,
        videoHeight
      } = video;
      if (videoWidth && videoHeight) {
        setVideoRatio(`${videoWidth}/${videoHeight}`);
      }
    }
    getRatio();
    video.addEventListener("loadedmetadata", getRatio);
    return () => {
      video.removeEventListener("loadedmetadata", getRatio);
    };
  }, [video]);
  if (!craftMedia) {
    return null;
  }
  const imageRatio = sectionContent.useOriginalRatio ? undefined : toSupportedRatio(sectionContent.media?.aspectRatio) || "2/1";
  return <_StyledSection {...sectionAttrs(section)} data-theme={mode} $_css={[
  // v3: white theme renders true white (bg-primary is mode-aware); grey untouched.
  {
    "color": "var(--text-primary)"
  }, theme === "grey" ? {
    "backgroundColor": "var(--bg-tertiary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  }, sectionSettings?.hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, sectionContent.isHighlighted && {
    "backgroundColor": "var(--white-static)"
  },
  // Positioned sections paint above the absolutely-positioned page pattern.
  sectionContent.hasSolidBackground && {
    "position": "relative"
  }]}>
      <_StyledWrap>
        <_StyledDiv $_css2={[{
        "padding": "1.6rem",
        "@media (min-width: 1024px)": {
          "padding": "4rem"
        }
      }, assetLayoutVariants[assetLayout]]}>
          <Media {...craftMedia} showCaption imageProps={{
          ratio: imageRatio
        }} inlineVideoProps={{
          ratio: videoRatio as Ratio,
          videoRef: setVideoRef,
          controls: <_StyledVideoControls videoRef={videoRef} />
        }} externalVideoProps={{
          ratio: "2/1"
        }} />
        </_StyledDiv>
      </_StyledWrap>
    </_StyledSection>;
}
function FullBleedMedia({
  section
}: Props) {
  const {
    sectionContent,
    sectionSettings
  } = section;
  const mode = sectionSettings?.mode || "light";
  const theme = sectionSettings?.theme || "white";
  const craftMedia = toCraftMedia(sectionContent.media);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  if (!craftMedia) {
    return null;
  }
  return <_StyledSection2 {...sectionAttrs(section)} data-theme={mode} $_css3={[
  // v3: white theme renders true white (bg-primary is mode-aware); grey untouched.
  {
    "color": "var(--text-primary)"
  }, theme === "grey" ? {
    "backgroundColor": "var(--bg-tertiary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  }, theme === "grey" && mode === "dark" && {
    "backgroundColor": "var(--bg-primary)"
  }, sectionContent.isHighlighted && {
    "backgroundColor": "var(--white-static)"
  },
  // Positioned sections paint above the absolutely-positioned page pattern.
  sectionContent.hasSolidBackground && {
    "position": "relative"
  }]}>
      <_StyledDiv2>
        <Media {...craftMedia} imageProps={{
        fit: "cover",
        rounded: false
      }} inlineVideoProps={{
        rounded: false,
        fit: "cover",
        videoRef,
        controls: <_StyledVideoControls2 videoRef={videoRef} />
      }} externalVideoProps={{
        rounded: false,
        fit: "cover"
      }} />
      </_StyledDiv2>
    </_StyledSection2>;
}
export function SanityMediaOnlySection({
  section
}: Props) {
  if (!hasRenderableSanityMedia(section.sectionContent.media)) {
    return null;
  }

  // Unknown or missing layouts render in the boxed presentation.
  if (section.sectionContent.layout === "full-bleed") {
    return <FullBleedMedia section={section} />;
  }
  return <BoxedMedia section={section} />;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-1nbfhc2-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-1nbfhc2-1"
})({
  "borderWidth": "0px"
});
var _StyledDiv = _styled3("div").withConfig({
  componentId: "sc-sf-1nbfhc2-2"
})`${p => p.$_css2}`;
var _StyledVideoControls = _styled4(VideoControls).withConfig({
  componentId: "sc-sf-1nbfhc2-3"
})({
  "position": "absolute",
  "right": "1.6rem",
  "top": "1.6rem",
  "zIndex": "10"
});
var _StyledSection2 = _styled5("section").withConfig({
  componentId: "sc-sf-1nbfhc2-4"
})`${p => p.$_css3}`;
var _StyledDiv2 = _styled6("div").withConfig({
  componentId: "sc-sf-1nbfhc2-5"
})({
  "position": "relative",
  "height": "64rem",
  "width": "100%",
  "@media (min-width: 1024px)": {
    "height": "89.6rem"
  }
});
var _StyledVideoControls2 = /*#__PURE__*/_styled7(VideoControls).withConfig({
  displayName: "media-only___StyledVideoControls2",
  componentId: "sc-1gtxkt-0"
})({
  "position": "absolute",
  "right": "1.6rem",
  "top": "1.6rem",
  "zIndex": "10"
});